import React, { useState, useEffect } from 'react';
import { X, AlertCircle, ArrowRight } from 'lucide-react';
import BolUploadModal from '../../components/BolUploadModal';
import logiwaOrderService from '../services/logiwaOrderService';
import { parseRefList } from '../../components/utils/refListUtils';

/**
 * LogiwaCompleteWizard
 *
 * 3-step wizard mounted when the operator clicks Complete on a non-small-parcel
 * outbound truck. Owns the order-number prompt, the conditional cost+tracking
 * form, and the existing BolUploadModal as the final step.
 *
 * Submission goes to POST /api/outbound-trucks/:id/complete-with-bol with the
 * Logiwa fields embedded in the JSON `data` form-data field. The local Complete
 * + BOL flow is unchanged for trucks not found in Logiwa.
 *
 * Props:
 *   isOpen     boolean
 *   onClose    () => void                — called on cancel or success
 *   truck      object                    — the outbound truck row
 *   onComplete (responseJson) => void    — called after a successful submit
 */
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Keep in sync with COST_MARKUP_BY_CLIENT / COST_MARKUP_DEFAULT in
// server/controllers/outboundTruckScheduleController.js. Server is authoritative;
// this mirror exists only for the live preview hint under the cost input.
const previewMarkup = (rawCost, clientDisplayName) => {
  const key = (clientDisplayName || '').trim().toLowerCase();
  const percent = key === 'master tool repair' ? 0.15 : 0.25;
  const num = Number(rawCost);
  if (!Number.isFinite(num) || num < 0) return null;
  return { percent, marked: Math.round(num * (1 + percent) * 100) / 100 };
};

const LogiwaCompleteWizard = ({ isOpen, onClose, truck, onComplete }) => {
  const [step, setStep] = useState('order_number');
  const [orderNumber, setOrderNumber] = useState('');
  const [lookup, setLookup] = useState(null);
  const [cost, setCost] = useState('');
  const [tracking, setTracking] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset state when the modal opens for a new truck.
  useEffect(() => {
    if (isOpen) {
      setStep('order_number');
      // Logiwa is one order per truck, so only prefill when the truck actually holds one order
      // number. Multi-order trucks store a comma-separated list (see Multi_Order_Outbound_Trucks.md);
      // prefilling that would send the whole list to Logiwa's listByCode and guarantee a
      // not-found. Leave it blank instead and let the operator type the single Logiwa order.
      // Coerce to string — legacy trucks may still hold a numeric order_number.
      const truckOrders = parseRefList(truck?.order_number);
      setOrderNumber(truckOrders.length === 1 ? String(truckOrders[0]) : '');
      setLookup(null);
      setCost('');
      setTracking('');
      setError('');
      setBusy(false);
    }
  }, [isOpen, truck]);

  if (!isOpen) return null;

  // Orders recorded on the truck. Logiwa is one order per truck, so >1 means this truck can't be
  // auto-matched and the operator has to name the single Logiwa order (or skip Logiwa).
  const truckOrderNumbers = parseRefList(truck?.order_number);

  const closeAll = () => {
    setBusy(false);
    onClose();
  };

  // ---- Step 1: order number lookup --------------------------------------
  const handleLookup = async () => {
    if (!orderNumber || !orderNumber.trim()) {
      setError('Order number is required to continue.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await logiwaOrderService.lookupLogiwaOrder(truck.id, orderNumber.trim());
      setLookup(res);
      setBusy(false);
      if (!res.enabled) {
        // Integration disabled server-side — go straight to BOL upload.
        setStep('bol_upload');
        return;
      }
      if (res.found) {
        // Pre-fill cost from Logiwa if it's set.
        if (res.totalShippingCost !== null && res.totalShippingCost !== undefined) {
          setCost(String(res.totalShippingCost));
        }
        setStep('cost_tracking');
      } else {
        setStep('bol_upload');
      }
    } catch (err) {
      setBusy(false);
      setError(err.message || 'Logiwa lookup failed');
    }
  };

  const handleSkipLogiwa = () => {
    // Proceed local-only. Set lookup to null so submit doesn't include Logiwa fields.
    setLookup(null);
    setError('');
    setStep('bol_upload');
  };

  // ---- Step 2: cost + tracking ------------------------------------------
  const costAlreadySet =
    lookup && lookup.totalShippingCost !== null && lookup.totalShippingCost !== undefined;

  const handleCostTrackingContinue = () => {
    if (!costAlreadySet) {
      const num = Number(cost);
      if (!cost || Number.isNaN(num) || num < 0) {
        setError('Total shipping cost is required.');
        return;
      }
    }
    setError('');
    setStep('bol_upload');
  };

  // ---- Step 3: BOL upload + final submit --------------------------------
  // Build the JSON `data` payload sent alongside the (optional) BOL file. Shared
  // between the upload path and the skip path so they stay in lockstep.
  //
  // KSP convention: the BOL/Pro number is the same value as the tracking number. The wizard
  // collects it once on Step 2 (`tracking` state) and we fan it out to both Logiwa fields
  // server-side. The `bolNumber` arg is kept for backwards compat with the modal but is
  // ignored when the wizard provides a tracking value.
  const buildDataPayload = (bolNumber) => {
    const dataPayload = {
      completed_at: new Date().toISOString(),
    };
    const trimmedTracking = tracking ? tracking.trim() : '';
    const effectiveBol = bolNumber || trimmedTracking;
    if (effectiveBol) dataPayload.bol_number = effectiveBol;
    if (lookup && lookup.found && lookup.identifier) {
      dataPayload.logiwa_shipment_order_identifier = lookup.identifier;
      dataPayload.logiwa_shipment_order_code = orderNumber.trim();
      // Echo the client display name so the server can resolve the markup rate without re-GETting the order.
      if (lookup.clientDisplayName) dataPayload.client_display_name = lookup.clientDisplayName;
      // Only push cost back if it's newly entered (Logiwa cost was null). Server applies the
      // per-client markup before sending to Logiwa — we send the raw entered value.
      if (!costAlreadySet && cost) {
        dataPayload.cost = Number(cost);
        dataPayload.cost_currency = lookup.currentCarrierCurrencyCode || 'USD';
      }
      if (trimmedTracking) dataPayload.tracking_number = trimmedTracking;
    }
    return dataPayload;
  };

  const submitComplete = async (truckId, formData) => {
    const response = await fetch(`${API_URL}/outbound-trucks/${truckId}/complete-with-bol`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    });

    const respJson = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(respJson.message || 'Failed to complete truck');
      err.body = respJson;
      throw err;
    }

    if (onComplete) onComplete(respJson);
    closeAll();
    return respJson;
  };

  // Replaces BolUploadModal's onUpload. We assemble the full /complete-with-bol payload here.
  const handleBolUpload = async (truckId, file, bolNumber) => {
    const formData = new FormData();
    formData.append('bolFile', file);
    formData.append('data', JSON.stringify(buildDataPayload(bolNumber)));
    return submitComplete(truckId, formData);
  };

  // Skip path: completes the truck without a BOL file. Operator will upload from History later.
  const handleSkipBol = async (truckId, bolNumber) => {
    const formData = new FormData();
    formData.append('data', JSON.stringify(buildDataPayload(bolNumber)));
    return submitComplete(truckId, formData);
  };

  // ---- render ------------------------------------------------------------

  // Step 3 is the existing BolUploadModal; render it directly. The wizard already collected
  // the BOL/Pro number on Step 2 (it shares the field with tracking), so hide that input in
  // the modal to avoid asking twice.
  if (step === 'bol_upload') {
    return (
      <BolUploadModal
        isOpen={true}
        onClose={closeAll}
        onUpload={handleBolUpload}
        onSkip={handleSkipBol}
        truckId={truck.id}
        truckInfo={truck}
        hideBolNumber={true}
      />
    );
  }

  // Steps 1 + 2 share a small modal shell.
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-lg max-w-md w-full shadow-xl">
        <div className="flex justify-between items-center p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-slate-200">
            {step === 'order_number' ? 'Complete LTL Truck — Order Number' : 'Complete LTL Truck — Cost & Tracking'}
          </h3>
          <button className="text-slate-400 hover:text-slate-200" onClick={closeAll} disabled={busy}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {truck && (
            <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="text-sm text-slate-400">Completing:</div>
              <div className="text-slate-200 font-medium">{truck.carrier}</div>
              {truck.customer && (
                <div className="text-sm text-slate-400">Customer: {truck.customer}</div>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" />
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          {step === 'order_number' && (
            <>
              <p className="text-sm text-slate-300">
                Enter the Logiwa order number for this truck. We'll look it up before continuing.
              </p>
              {truckOrderNumbers.length > 1 && (
                <div className="flex items-start bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
                  <AlertCircle className="w-4 h-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-amber-200">
                    <p className="font-medium mb-1">
                      This truck has {truckOrderNumbers.length} order numbers.
                    </p>
                    <p className="text-amber-200/80">
                      Logiwa handles one order per truck, so we didn't prefill anything. Enter the
                      single Logiwa order number, or skip Logiwa and complete locally only.
                    </p>
                    <p className="mt-1 text-amber-200/60 break-words">
                      On this truck: {truckOrderNumbers.join(', ')}
                    </p>
                  </div>
                </div>
              )}
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                disabled={busy}
                placeholder="e.g. SO0004"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                autoFocus
              />
              {/* Offered after a failed lookup, and up front on multi-order trucks — those have no
                  single order to prefill, so completing locally is a legitimate first choice. */}
              {(error || truckOrderNumbers.length > 1) && (
                <button
                  type="button"
                  onClick={handleSkipLogiwa}
                  className="text-xs text-slate-400 hover:text-slate-200 underline"
                >
                  Skip Logiwa and complete locally only
                </button>
              )}
            </>
          )}

          {step === 'cost_tracking' && lookup && lookup.found && (
            <>
              <div>
                <label className="block text-sm text-slate-300 mb-1">
                  Total Shipping Cost {costAlreadySet ? <span className="text-slate-500 text-xs">(already set in Logiwa — read only)</span> : <span className="text-red-400">*</span>}
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  readOnly={costAlreadySet}
                  disabled={busy}
                  placeholder="0.00"
                  className={`w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
                    costAlreadySet ? 'bg-slate-900/40 cursor-not-allowed' : ''
                  }`}
                />
                {!costAlreadySet && cost && (() => {
                  const p = previewMarkup(cost, lookup.clientDisplayName);
                  if (!p) return null;
                  const clientLabel = lookup.clientDisplayName || 'default rate';
                  return (
                    <div className="text-xs text-slate-400 mt-1">
                      Logiwa receives: <span className="text-slate-200">${p.marked.toFixed(2)}</span>
                      <span className="text-slate-500"> ({clientLabel} markup: {(p.percent * 100).toFixed(0)}%)</span>
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">
                  BOL / Pro Number <span className="text-slate-500 text-xs">(optional — sent to Logiwa as both Tracking Number and BOL Number)</span>
                </label>
                <input
                  type="text"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  disabled={busy}
                  placeholder="e.g. PRO-12345 or BOL-12345"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-slate-700 flex justify-end space-x-3">
          <button
            type="button"
            onClick={closeAll}
            disabled={busy}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          {step === 'order_number' && (
            <button
              type="button"
              onClick={handleLookup}
              disabled={busy}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm disabled:opacity-50 flex items-center gap-1"
            >
              {busy ? 'Looking up…' : (<>Continue <ArrowRight className="w-4 h-4" /></>)}
            </button>
          )}
          {step === 'cost_tracking' && (
            <button
              type="button"
              onClick={handleCostTrackingContinue}
              disabled={busy}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm disabled:opacity-50 flex items-center gap-1"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogiwaCompleteWizard;
